import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import JobCard from "./JobCard";
import classes from "./styles/SEOPageDetails.module.css";
import { getSEOPageBySlug, getJobsByLocation, deriveJobKeyword } from "../../../services/seoPageService";

// ── Structured data helpers ──────────────────────────────────
const buildJobPostingSchema = (job, seoData) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  "title": job.title,
  "hiringOrganization": { "@type": "Organization", "name": job.company || "" },
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": seoData.location || "",
      "addressCountry": seoData.country || ""
    }
  },
  "datePosted": job.datePosted || "",
  "employmentType": job.employmentType || "FULL_TIME",
  "description": job.summary || "",
  "url": `https://www.fetchjobs.co/job/description/${job.JobId}?country=${seoData.country}`
});

const buildFAQSchema = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faq.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
});

const buildBreadcrumbSchema = (seoData) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.fetchjobs.co" },
    { "@type": "ListItem", "position": 2, "name": "Jobs", "item": "https://www.fetchjobs.co/jobs" },
    { "@type": "ListItem", "position": 3, "name": seoData.title , "item": `https://www.fetchjobs.co/jobs/${seoData.slug}` }
  ]
});

// ── FAQ accordion ────────────────────────────────────────────
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${classes.faqItem} ${open ? classes.faqOpen : ""}`}>
      <button className={classes.faqQuestion} onClick={() => setOpen((o) => !o)}>
        <span>{question}</span>
        <svg
          className={`${classes.faqIcon} ${open ? classes.faqIconOpen : ""}`}
          width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && <p className={classes.faqAnswer}>{answer}</p>}
    </div>
  );
};

// ── Skeleton — matches real card shape exactly ───────────────
const CardSkeleton = () => (
  <div className={classes.skeleton}>
    <div className={classes.skeletonTopRow}>
      <div className={classes.skeletonLogo} />
      <div className={classes.skeletonLines}>
        <div className={classes.skeletonTitle} />
        <div className={classes.skeletonMeta} />
      </div>
    </div>
    <div className={classes.skeletonSummaryWrap}>
      <div className={classes.skeletonSummary} />
      <div className={classes.skeletonSummary} style={{ width: "72%" }} />
    </div>
  </div>
);

const HeaderSkeleton = () => (
  <div className={classes.headerSkeletonWrap}>
    <div className={classes.headerSkeletonTitle} />
    <div className={classes.headerSkeletonSubtitle} />
  </div>
);

// ── Main component ───────────────────────────────────────────
const SEOPageDetails = () => {
  const { slug } = useParams();

  const [seoData, setSeoData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [seoLoading, setSeoLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [seoError, setSeoError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setSeoLoading(true);
    setSeoError(null);
    getSEOPageBySlug(slug)
      .then((data) => { setSeoData(data); setSeoLoading(false); })
      .catch((err) => { setSeoError(err.message); setSeoLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (!seoData) return;
    setJobsLoading(true);
    getJobsByLocation({
      title: deriveJobKeyword(seoData),
      country: seoData.country,
      location: seoData.location,
      page,
      limit: 20,
    })
      .then((res) => {
        setJobs(res.jobs || []);
        setTotalJobs(res.total || 0);
        setTotalPages(res.totalPages || 1);
        setJobsLoading(false);
      })
      .catch(() => setJobsLoading(false));
  }, [seoData, page]);

  // Full-page skeleton while SEO data loads
  if (seoLoading) {
    return (
      <div className={classes.page}>
        <div className={classes.header}>
          <HeaderSkeleton />
        </div>
        <div className={classes.layout}>
          <div className={classes.main}>
            {[1, 2, 3, 4, 5].map(i => <CardSkeleton key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (seoError) {
    return (
      <div className={classes.page}>
        <div className={classes.errorBox}>Could not load this page. Please try again later.</div>
      </div>
    );
  }

  const canonicalUrl = `https://www.fetchjobs.co/jobs/${slug}`;

  return (
    <div className={classes.page}>
      {/* ── Technical SEO ── */}
      <Helmet>
        {/* Core meta */}
        <title>{seoData.meta_title || seoData.title}</title>
        <meta name="description" content={seoData.meta_description || seoData.subtitle} />
        {seoData.keywords?.length > 0 && (
          <meta name="keywords" content={seoData.keywords.join(", ")} />
        )}

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoData.meta_title || seoData.title} />
        <meta property="og:description" content={seoData.meta_description || seoData.subtitle} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="FetchJobs" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoData.meta_title || seoData.title} />
        <meta name="twitter:description" content={seoData.meta_description || seoData.subtitle} />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* Breadcrumb schema */}
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumbSchema(seoData))}
        </script>

        {/* FAQ schema */}
        {seoData.faq?.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(buildFAQSchema(seoData.faq))}
          </script>
        )}

        {/* JobPosting schema for first 5 jobs (enough for Google) */}
        {jobs.slice(0, 5).map((job, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(buildJobPostingSchema(job, seoData))}
          </script>
        ))}
      </Helmet>

      {/* ── Page Header ── */}
      <div className={classes.header}>
        <h1 className={classes.pageTitle}>{seoData.title}</h1>
        {seoData.subtitle && <p className={classes.pageSubtitle}>{seoData.subtitle}</p>}
      </div>

      {/* ── Body ── */}
      <div className={classes.layout}>
        <div className={classes.main}>

          {/* Jobs count bar */}
          {!jobsLoading && totalJobs > 0 && (
            <div className={classes.countBar}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              </svg>
              <span>{totalJobs} jobs found{seoData.location ? ` in ${seoData.location}` : ""}</span>
            </div>
          )}

          {/* Job list */}
          {jobsLoading ? (
            [1, 2, 3, 4, 5].map(i => <CardSkeleton key={i} />)
          ) : jobs.length === 0 ? (
            <div className={classes.noJobs}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#9baac4" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              </svg>
              <p>No jobs found right now. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className={classes.jobList}>
                {jobs.map((job) => (
                  <JobCard key={job._id || job.JobId} job={job} country={seoData.country} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className={classes.pagination}>
                  <button
                    className={classes.pageBtn}
                    disabled={page === 1}
                    onClick={() => { setPage(p => p - 1); window.scrollTo(0, 0); }}
                  >
                    ← Previous
                  </button>
                  <span className={classes.pageInfo}>Page {page} of {totalPages}</span>
                  <button
                    className={classes.pageBtn}
                    disabled={page === totalPages}
                    onClick={() => { setPage(p => p + 1); window.scrollTo(0, 0); }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── SEO Content block (for Google) ── */}
          {(seoData.content || seoData.faq?.length > 0) && (
            <div className={classes.seoSection}>
              {seoData.content && (
                <div
                  className={classes.seoContent}
                  dangerouslySetInnerHTML={{ __html: seoData.content.replace(/<br\s*\/?>/gi, "") }}
                />
              )}

              {seoData.faq?.length > 0 && (
                <div className={classes.faqBlock}>
                  <h2 className={classes.faqHeading}>Frequently Asked Questions</h2>
                  <div className={classes.faqList}>
                    {seoData.faq.map((item, idx) => (
                      <FAQItem key={idx} question={item.question} answer={item.answer} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Related slugs */}
          {seoData.related_slugs?.length > 0 && (
            <div className={classes.relatedBlock}>
              <h3 className={classes.relatedHeading}>Related Job Categories</h3>
              <div className={classes.relatedList}>
                {seoData.related_slugs.map((rel) => (
                  <a key={rel.slug} href={rel.slug} className={classes.relatedChip}>
                    {rel.title}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SEOPageDetails;
